import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API, authFetch, useAuth } from "@/App";
import PageLayout, { PageHeader, Card } from "@/components/PageLayout";
import { 
  ArrowLeft, Lightbulb, Heart, Utensils, GraduationCap, 
  Sparkles, ChevronRight, Dog, Cat, Clock, Bookmark, Share2
} from "lucide-react";

const categoryConfig = {
  nutrition: { name: "Alimentazione", icon: Utensils, color: "from-orange-500 to-amber-600", lightBg: "bg-orange-50", textColor: "text-orange-600" },
  health: { name: "Salute", icon: Heart, color: "from-red-500 to-rose-600", lightBg: "bg-red-50", textColor: "text-red-600" },
  training: { name: "Educazione", icon: GraduationCap, color: "from-blue-500 to-indigo-600", lightBg: "bg-blue-50", textColor: "text-blue-600" },
  care: { name: "Cura", icon: Sparkles, color: "from-purple-500 to-violet-600", lightBg: "bg-purple-50", textColor: "text-purple-600" }
};

const speciesIcons = { dog: Dog, cat: Cat };

export default function Tips() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tipsData, setTipsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [expandedTip, setExpandedTip] = useState(null);

  useEffect(() => {
    setIsMobile(window.innerWidth < 1024);
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    fetchTips();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchTips = async () => {
    try {
      let response;
      if (user) {
        response = await authFetch(`${API}/tips/for-me`);
      } else {
        response = await fetch(`${API}/tips/dog`);
      }
      
      if (response.ok) {
        const data = await response.json();
        setTipsData(data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="animate-pulse">
          <Lightbulb size={48} className="text-amber-500" />
        </div>
      </div>
    );
  }

  const tips = tipsData?.tips || [];
  const filteredTips = selectedCategory 
    ? tips.filter(t => t.category === selectedCategory) 
    : tips;

  // Desktop Layout
  if (!isMobile) {
    return (
      <PageLayout data-testid="tips-page">
        <PageHeader 
          title="Consigli"
          subtitle={tipsData?.personalized 
            ? `Personalizzati per i tuoi ${tipsData?.pets_count} animali`
            : "Guide e consigli per il tuo animale"
          }
          icon={Lightbulb}
          iconColor="#F59E0B"
        />

        <div className="grid grid-cols-12 gap-6">
            {/* Sidebar - Categories */}
            <div className="col-span-3">
              <div className="bg-white rounded-3xl p-5 shadow-sm border border-black/5 sticky top-8">
                <h2 className="font-bold text-[#1D1D1F] mb-4">Categorie</h2>
                
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl mb-2 transition-all ${
                    !selectedCategory ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white' : 'hover:bg-[#F5F5F7]'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    !selectedCategory ? 'bg-white/20' : 'bg-amber-50'
                  }`}>
                    <Sparkles size={20} className={!selectedCategory ? 'text-white' : 'text-amber-600'} />
                  </div>
                  <span className="font-medium">Tutti</span>
                </button>
                
                {Object.entries(categoryConfig).map(([key, cat]) => {
                  const Icon = cat.icon;
                  const isSelected = selectedCategory === key;
                  
                  return (
                    <button
                      key={key}
                      onClick={() => setSelectedCategory(isSelected ? null : key)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl mb-2 transition-all ${
                        isSelected ? `bg-gradient-to-r ${cat.color} text-white` : 'hover:bg-[#F5F5F7]'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        isSelected ? 'bg-white/20' : cat.lightBg
                      }`}>
                        <Icon size={20} className={isSelected ? 'text-white' : cat.textColor} />
                      </div>
                      <span className="font-medium">{cat.name}</span>
                    </button>
                  );
                })}

                {/* Pet species */}
                {tipsData?.pets_count > 0 && (
                  <div className="mt-6 pt-4 border-t border-black/5">
                    <p className="text-sm text-[#86868B] mb-3">I tuoi animali</p>
                    <div className="flex gap-2">
                      <div className="flex items-center gap-2 bg-[#F5F5F7] rounded-xl px-3 py-2">
                        <Dog size={16} className="text-[#FF6B4A]" />
                        <span className="text-sm text-[#6E6E73]">Cani</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Main Content */}
            <div className="col-span-9">
              <div className="bg-white rounded-3xl p-6 shadow-sm border border-black/5">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-[#1D1D1F]">
                    {selectedCategory ? categoryConfig[selectedCategory]?.name : 'Tutti i consigli'}
                  </h2>
                  <span className="text-[#86868B]">{filteredTips.length} articoli</span>
                </div>

                {filteredTips.length === 0 ? (
                  <div className="text-center py-12">
                    <Lightbulb className="mx-auto text-[#D1D1D6] mb-4" size={48} />
                    <h3 className="font-semibold text-[#1D1D1F] mb-2">Nessun consiglio</h3>
                    <p className="text-[#86868B]">Non ci sono consigli per questa categoria</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredTips.map((tip, index) => {
                      const catConfig = categoryConfig[tip.category] || categoryConfig.care;
                      const Icon = catConfig.icon;
                      const isExpanded = expandedTip === index;
                      
                      return (
                        <div 
                          key={index}
                          className="bg-[#F5F5F7] rounded-2xl p-5 hover:bg-[#EBEBEB] transition-all cursor-pointer"
                          onClick={() => setExpandedTip(isExpanded ? null : index)}
                        >
                          <div className="flex items-start gap-4">
                            <div className={`w-14 h-14 rounded-2xl ${catConfig.lightBg} flex items-center justify-center flex-shrink-0`}>
                              <Icon size={28} className={catConfig.textColor} />
                            </div>
                            
                            <div className="flex-1">
                              <div className="flex items-start justify-between mb-2">
                                <h3 className="font-bold text-[#1D1D1F] text-lg">{tip.title}</h3>
                                <div className="flex items-center gap-2">
                                  {tip.species && (
                                    <span className="text-xs bg-white px-2 py-1 rounded-lg text-[#6E6E73] capitalize">
                                      {tip.species === 'dog' ? 'Cani' : 'Gatti'}
                                    </span>
                                  )}
                                  <button className="p-2 hover:bg-white rounded-lg transition-colors">
                                    <Bookmark size={16} className="text-[#86868B]" />
                                  </button>
                                </div>
                              </div>
                              
                              <p className={`text-[#6E6E73] ${isExpanded ? '' : 'line-clamp-2'}`}>
                                {tip.content}
                              </p>
                              
                              {isExpanded && (
                                <div className="mt-4 pt-4 border-t border-black/5 flex items-center gap-4">
                                  <div className="flex items-center gap-2 text-sm text-[#86868B]">
                                    <Clock size={14} />
                                    <span>2 min lettura</span>
                                  </div>
                                  <button className="flex items-center gap-2 text-sm text-[#FF6B4A] font-medium hover:underline">
                                    <Share2 size={14} />
                                    Condividi
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </PageLayout>
    );
  }

  // Mobile Layout
  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-28" data-testid="tips-page-mobile">
      {/* Header */}
      <div className="bg-gradient-to-br from-amber-400 via-orange-400 to-amber-500 text-white px-5 pt-6 pb-10 rounded-b-[2rem]">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
            <ArrowLeft className="text-white" size={20} />
          </button>
          <div>
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Consigli per te
            </h1>
            <p className="text-amber-100 text-sm">
              {tipsData?.personalized 
                ? `Personalizzati per i tuoi ${tipsData?.pets_count} animali`
                : "Guide e consigli"
              }
            </p>
          </div>
        </div>
        
        {/* Category Pills */}
        <div className="flex gap-2 flex-wrap mt-4">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap ${
              !selectedCategory ? 'bg-white text-amber-600' : 'bg-white/20 text-white'
            }`}
          >
            Tutti
          </button>
          {Object.entries(categoryConfig).map(([key, cat]) => {
            const Icon = cat.icon;
            return (
              <button
                key={key}
                onClick={() => setSelectedCategory(selectedCategory === key ? null : key)}
                className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap flex items-center gap-2 ${
                  selectedCategory === key ? 'bg-white text-amber-600' : 'bg-white/20 text-white'
                }`}
              >
                <Icon size={14} />
                {cat.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tips List */}
      <div className="px-5 -mt-4 space-y-3">
        {filteredTips.map((tip, index) => {
          const catConfig = categoryConfig[tip.category] || categoryConfig.care;
          const Icon = catConfig.icon;
          
          return (
            <div 
              key={index}
              className="bg-white rounded-2xl p-4 shadow-sm border border-black/5"
            >
              <div className="flex items-start gap-3">
                <div className={`w-12 h-12 rounded-xl ${catConfig.lightBg} flex items-center justify-center flex-shrink-0`}>
                  <Icon size={24} className={catConfig.textColor} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-[#1D1D1F] mb-1">{tip.title}</h3>
                  <p className="text-sm text-[#6E6E73] line-clamp-3">{tip.content}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
